// Libs
import React, {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { Paper, Typography, Box, Chip } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  ICellRendererParams,
  RowClickedEvent,
} from "ag-grid-community";
import {
  ExistingEmailCampaign,
  EmailCampaignStatus,
  EmailCampaignManager,
} from "../../models/email-campaign";
import { DbRead } from "../../database";
import {
  EMAIL_CAMPAIGNS_CREATE_URL,
  EMAIL_CAMPAIGNS_DETAILS_URL,
  EMAIL_MARKETING_URL,
} from "../../urls";
import { ArrowLeftIcon, PlusIcon } from "@heroicons/react/24/outline";

// Local
import BaseButtonPrimary from "../../components/BaseButtonPrimary";
import BaseButtonSecondary from "../../components/BaseButtonSecondary";

// Styles
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";

interface Props {
  siteKey: string;
}

export default function EmailCampaignsListContainer({ siteKey }: Props) {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<ExistingEmailCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gridReady, setGridReady] = useState(false);
  const gridRef = useRef<any>(null);

  const onGridReady = useCallback(() => {
    setGridReady(true);
  }, []);

  useLayoutEffect(() => {
    if (gridRef.current && gridReady) {
      gridRef.current.api?.sizeColumnsToFit();
    }
  }, [gridReady]);

  useEffect(() => {
    const unsubscribeCampaigns = DbRead.emailCampaigns.subscribe({
      siteKey,
      onChange: (campaignsData) => {
        setCampaigns(campaignsData);
        setLoading(false);
        setError(null);
      },
      onError: (err) => {
        console.error("Failed to fetch email campaigns:", err);
        setError("Failed to load email campaigns");
        setLoading(false);
      },
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribeCampaigns();
    };
  }, [siteKey]);

  const handleCreateCampaign = () => {
    navigate(EMAIL_CAMPAIGNS_CREATE_URL);
  };

  const handleViewCampaign = (campaignId: string) => {
    navigate(EMAIL_CAMPAIGNS_DETAILS_URL.replace(":campaignId", campaignId));
  };

  const handleBackToEmailMarketing = () => {
    navigate(EMAIL_MARKETING_URL);
  };

  const handleRowClicked = (event: RowClickedEvent) => {
    const campaign = event.data as ExistingEmailCampaign;
    handleViewCampaign(campaign.id);
  };

  const getStatusColor = (status: EmailCampaignStatus) => {
    switch (status) {
      case EmailCampaignStatus.COMPLETED:
        return "success";
      case EmailCampaignStatus.SENDING:
        return "info";
      case EmailCampaignStatus.PENDING:
        return "warning";
      case EmailCampaignStatus.FAILED:
        return "error";
      case EmailCampaignStatus.CANCELLED:
        return "default";
      default:
        return "default";
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp || !timestamp.toDate) return "N/A";
    return timestamp.toDate().toLocaleDateString();
  };

  const renderStatusCell = (params: ICellRendererParams): JSX.Element => {
    return (
      <Chip
        label={params.value}
        color={getStatusColor(params.value) as any}
        size="small"
        variant="outlined"
      />
    );
  };

  const renderSubjectCell = (params: ICellRendererParams): JSX.Element => {
    const campaign = params.data as ExistingEmailCampaign;
    return (
      <span className="text-sm text-gray-900" title={campaign.renderedSubject}>
        {campaign.renderedSubject || "—"}
      </span>
    );
  };

  const renderDateCell = (params: ICellRendererParams): JSX.Element => {
    return <span>{formatDate(params.value)}</span>;
  };

  const columnDefs: ColDef[] = [
    {
      field: "campaignName",
      headerName: "Campaign Name",
      flex: 2,
      tooltipValueGetter: (params) => params.value,
    },
    {
      field: "templateName",
      headerName: "Template",
      flex: 1,
      tooltipValueGetter: (params) => params.value,
    },
    {
      headerName: "Subject Line",
      flex: 2,
      cellRenderer: renderSubjectCell,
      sortable: false,
      filter: false,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      cellRenderer: renderStatusCell,
    },
    {
      field: "totalRecipients",
      headerName: "Recipients",
      flex: 1,
      type: "numericColumn",
    },
    {
      field: "timestampCreated",
      headerName: "Created",
      flex: 1,
      cellRenderer: renderDateCell,
    },
    {
      field: "timestampStarted",
      headerName: "Started",
      flex: 1,
      cellRenderer: renderDateCell,
    },
  ];

  const commonColProps = {
    resizable: true,
    filter: true,
    sortable: true,
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, textAlign: "center" }}>
        <Typography>Loading email campaigns...</Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, textAlign: "center" }}>
        <Typography color="error">{error}</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <div className="flex items-center space-x-4">
          <BaseButtonSecondary
            onClick={handleBackToEmailMarketing}
            className="flex items-center gap-2"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to Email Marketing
          </BaseButtonSecondary>
          <Typography variant="h5">Email Campaigns</Typography>
        </div>
        <BaseButtonPrimary
          onClick={handleCreateCampaign}
          className="flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          Create Campaign
        </BaseButtonPrimary>
      </Box>

      <div className="ag-theme-alpine" style={{ height: 600, width: "100%" }}>
        <AgGridReact
          ref={gridRef}
          onGridReady={onGridReady}
          onRowClicked={handleRowClicked}
          rowData={campaigns}
          columnDefs={columnDefs}
          defaultColDef={commonColProps}
          animateRows={true}
          rowSelection="single"
          getRowId={(params) => params.data.id}
          rowStyle={{ cursor: "pointer" }}
        />
      </div>
    </Paper>
  );
}
